import { Box, SxProps, Typography } from "@mui/material";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import React from "react";
import Underline from "@tiptap/extension-underline";
type MenuProps = {
  editor: Editor | null;
};

const ButtonStyle: SxProps = {
  height: "24px",
  borderRadius: "4px",
  width: "fit-content",
  minWidth: "30px",
  padding: "2px 4px",
  fontWeight: "bold",
  cursor: "pointer",
  textAlign: "center",
};

const ActiveButtonStyle: SxProps = {
  ...ButtonStyle,
  backgroundColor: "divider",
};

const MenuBarStyle: SxProps = {
  border: 1,
  borderColor: "divider",
  display: "flex",
  flexWrap: "wrap",
  gap: "4px",
  padding: "8px",
  borderTopLeftRadius: "6px",
  borderTopRightRadius: "6px",
};

const CustomStarterKit = StarterKit.configure({
  paragraph: {
    HTMLAttributes: {
      style: "min-height: 1rem;",
    },
  },
  orderedList: {
    HTMLAttributes: {
      style: "margin: 0.25rem 0.5rem 0.5rem 0.5rem; padding: 0 0.75rem;",
    },
  },
  bulletList: {
    HTMLAttributes: {
      style: "margin: 0.25rem 0.5rem 0.5rem 0.5rem; padding: 0 0.75rem;",
    },
  },
});

const MenuBar = ({ editor }: MenuProps) => {
  if (!editor) {
    return null;
  }

  // Helper function to handle keyboard events for accessibility
  const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };

  return (
    <Box sx={MenuBarStyle}>
      <Box
        role="button"
        tabIndex={0}
        aria-label="Toggle bold"
        onClick={() => editor.chain().focus().toggleBold().run()}
        onKeyDown={(e) =>
          handleKeyDown(e, () => editor.chain().focus().toggleBold().run())
        }
        sx={editor.isActive("bold") ? ActiveButtonStyle : ButtonStyle}
      >
        B
      </Box>
      <Box
        role="button"
        tabIndex={0}
        aria-label="Toggle italic"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        onKeyDown={(e) =>
          handleKeyDown(e, () => editor.chain().focus().toggleItalic().run())
        }
        sx={editor.isActive("italic") ? ActiveButtonStyle : ButtonStyle}
      >
        <Typography sx={{ fontWeight: "bold", fontStyle: "italic" }}>
          I
        </Typography>
      </Box>
      <Box
        role="button"
        tabIndex={0}
        aria-label="Toggle underline"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        onKeyDown={(e) =>
          handleKeyDown(e, () => editor.chain().focus().toggleUnderline().run())
        }
        sx={editor.isActive("underline") ? ActiveButtonStyle : ButtonStyle}
      >
        <Typography sx={{ fontWeight: "bold", textDecoration: "underline" }}>
          U
        </Typography>
      </Box>
      <Box
        role="button"
        tabIndex={0}
        aria-label="Toggle strikethrough"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        onKeyDown={(e) =>
          handleKeyDown(e, () => editor.chain().focus().toggleStrike().run())
        }
        sx={editor.isActive("strike") ? ActiveButtonStyle : ButtonStyle}
      >
        <Typography sx={{ textDecoration: "line-through", fontWeight: "bold" }}>
          S
        </Typography>
      </Box>
      <Box
        role="button"
        tabIndex={0}
        aria-label="Toggle heading level 1"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        onKeyDown={(e) =>
          handleKeyDown(e, () =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          )
        }
        sx={
          editor.isActive("heading", { level: 1 })
            ? ActiveButtonStyle
            : ButtonStyle
        }
      >
        H1
      </Box>
      <Box
        role="button"
        tabIndex={0}
        aria-label="Toggle heading level 2"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        onKeyDown={(e) =>
          handleKeyDown(e, () =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          )
        }
        sx={
          editor.isActive("heading", { level: 2 })
            ? ActiveButtonStyle
            : ButtonStyle
        }
      >
        H2
      </Box>
      <Box
        role="button"
        tabIndex={0}
        aria-label="Toggle heading level 3"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        onKeyDown={(e) =>
          handleKeyDown(e, () =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          )
        }
        sx={
          editor.isActive("heading", { level: 3 })
            ? ActiveButtonStyle
            : ButtonStyle
        }
      >
        H3
      </Box>
    </Box>
  );
};

const TipTap = ({ initialValue, onChange, preview }: any) => {
  const tipTapEditor = useEditor({
    extensions: [CustomStarterKit, Underline],
    content: initialValue,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    editable: !preview,
  });

  return (
    <Box>
      <MenuBar editor={tipTapEditor} />
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderBottomLeftRadius: "6px",
          borderBottomRightRadius: "6px",
        }}
      >
        <EditorContent editor={tipTapEditor} />
      </Box>
    </Box>
  );
};

export default React.memo(TipTap);
